import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { DndContext, DragOverlay, closestCorners } from '@dnd-kit/core';
import { arrayMove } from '@dnd-kit/sortable';
import FieldsColumn from './pfFieldsSelector/FieldsColumn.jsx';
import { FieldCard } from './pfFieldsSelector/Field.jsx';
import Checkbox from './shared/Checkbox';
import { useDndSensors } from './shared/dndSortable';

const AVAILABLE_COLUMN = 'available-fields-column';
const CURRENT_COLUMN = 'current-fields-column';

export const handleDragEnd = ({
  activeId,
  overId,
  activeContainer,
  overContainer,
  currentFieldIds,
}) => {
  if (!overId || !overContainer) return { newCurrentFieldIds: currentFieldIds };

  // Same column, current → reorder
  if (activeContainer === CURRENT_COLUMN && overContainer === CURRENT_COLUMN) {
    if (activeId === overId) return { newCurrentFieldIds: currentFieldIds };
    const fromIndex = currentFieldIds.indexOf(activeId);
    const toIndex = currentFieldIds.indexOf(overId);
    if (fromIndex < 0 || toIndex < 0) return { newCurrentFieldIds: currentFieldIds };
    return { newCurrentFieldIds: arrayMove(currentFieldIds, fromIndex, toIndex) };
  }

  // Available → Current: add active at position of over (or end)
  if (activeContainer === AVAILABLE_COLUMN && overContainer === CURRENT_COLUMN) {
    if (currentFieldIds.includes(activeId)) return { newCurrentFieldIds: currentFieldIds };
    const insertAt = currentFieldIds.indexOf(overId);
    const next = currentFieldIds.slice();
    if (insertAt < 0) {
      next.push(activeId);
    } else {
      next.splice(insertAt, 0, activeId);
    }
    return { newCurrentFieldIds: next };
  }

  // Current → Available: remove from current
  if (activeContainer === CURRENT_COLUMN && overContainer === AVAILABLE_COLUMN) {
    return {
      newCurrentFieldIds: currentFieldIds.filter((id) => id !== activeId),
    };
  }

  // Available → Available: no-op (the available list isn't reorderable)
  return { newCurrentFieldIds: currentFieldIds };
};

const containerOf = (id, currentFieldIds) => {
  if (id === AVAILABLE_COLUMN || id === CURRENT_COLUMN) return id;
  return currentFieldIds.includes(id) ? CURRENT_COLUMN : AVAILABLE_COLUMN;
};

export default function PfFieldsSelector({
  allFieldsHash = {},
  allFieldIds,
  initialFieldsIds,
  initialAllowCrossDependency,
  showFieldEditLink,
}) {
  const [currentFieldIds, setCurrentFieldsIds] = useState(initialFieldsIds);
  const [allowCrossDependency, setAllowCrossDependency] = useState(initialAllowCrossDependency);
  const [activeId, setActiveId] = useState(null);

  const sensors = useDndSensors();

  const onDragStart = ({ active }) => {
    setActiveId(active?.id ?? null);
  };

  const onDragCancel = () => {
    setActiveId(null);
  };

  const onDragEnd = ({ active, over }) => {
    setActiveId(null);
    const draggedId = active?.id;
    const overId = over?.id;
    const activeContainer = active?.data?.current?.containerId
      || containerOf(draggedId, currentFieldIds);
    const overContainer = over?.data?.current?.containerId
      || (overId ? containerOf(overId, currentFieldIds) : null);

    const { newCurrentFieldIds } = handleDragEnd({
      activeId: draggedId,
      overId,
      activeContainer,
      overContainer,
      currentFieldIds,
    });
    setCurrentFieldsIds(newCurrentFieldIds);
  };

  const availableFieldsIds = () => allFieldIds.filter((id) => !currentFieldIds.includes(id));

  const fields = (fieldIds) => fieldIds.map((id) => allFieldsHash[id]);

  const missingFields = () => {
    if (allowCrossDependency) return [];
    const missingFieldPairs = [];
    const currentFields = fields(currentFieldIds);
    for (const field of currentFields) {
      if (field.parent_field_id && !currentFieldIds.includes(field.parent_field_id)) {
        const missingField = fields([field.parent_field_id])[0];
        missingFieldPairs.push({ addedField: field, missingField });
      }
    }
    return missingFieldPairs;
  };

  const missingFieldsError = () => {
    const fieldPairs = missingFields();
    if (fieldPairs.length === 0) return null;
    const missingFieldDetails = fieldPairs.map((data) => (
      <li key={data.addedField.id}>
        <span className="text-semibold"> {data.missingField.name} </span>
        should be added for the dependent field
        <span className="text-semibold"> {data.addedField.name} </span>
        to work correctly.
      </li>
    ));
    return (
      <div className="alert alert-danger">
        <span className="text-semibold">Found missing fields (highlighted below):</span>
        <ul className="text-danger">{missingFieldDetails}</ul>
      </div>
    );
  };

  const submitButton = () => {
    const disabled = currentFieldIds.length === 0 || missingFields().length > 0;
    return (
      <div className="heading-elements">
        <button type="submit" className="btn btn-primary btn-xs heading-btn" disabled={disabled}>
          Save
        </button>
      </div>
    );
  };

  const errorFieldIds = () => missingFields().map((fieldPair) => fieldPair.missingField.id);

  const handleChangeAllowCrossDependency = () => {
    setAllowCrossDependency(!allowCrossDependency);
  };

  return (
    <div style={{ height: '200vh' }}>
      {missingFieldsError()}
      <div className="form-group">
        <Checkbox
          id="allow-cross-dependency"
          label="Allow fields to be dependent across different parameters (NOTE: If this is checked, also enable the Field Mode of the related convention)"
          value={allowCrossDependency}
          name="parameter_format[allow_cross_dependency]"
          onChange={handleChangeAllowCrossDependency}
        />
      </div>
      <div className="sticky">
        <DndContext
          sensors={sensors}
          collisionDetection={closestCorners}
          onDragStart={onDragStart}
          onDragEnd={onDragEnd}
          onDragCancel={onDragCancel}
        >
          <div className="available-fields col-md-6 mt-5">
            <FieldsColumn
              columnId={AVAILABLE_COLUMN}
              title="Available Fields"
              subtitle="In alphabetical order"
              fields={fields(availableFieldsIds())}
              errorFieldIds={errorFieldIds()}
              styleType="default"
              showFieldEditLink={showFieldEditLink}
            />
          </div>
          <div className="current-fields col-md-6 mt-5">
            <FieldsColumn
              columnId={CURRENT_COLUMN}
              title="Configured Fields"
              subtitle="In desired order"
              fields={fields(currentFieldIds)}
              styleType="success"
              populateInputs={true}
              submitButton={submitButton()}
              showFieldEditLink={showFieldEditLink}
            />
          </div>
          <DragOverlay>
            {activeId && allFieldsHash[activeId] ? (
              // Wrap in the same `ul.list-unstyled` the columns use so the
              // floating card keeps valid markup and no list bullet shows.
              <ul className="list-unstyled" style={{ margin: 0 }}>
                <FieldCard
                  field={allFieldsHash[activeId]}
                  showFieldEditLink={showFieldEditLink}
                  dragging
                />
              </ul>
            ) : null}
          </DragOverlay>
        </DndContext>
      </div>
    </div>
  );
}

PfFieldsSelector.propTypes = {
  allFieldsHash: PropTypes.any.isRequired,
  allFieldIds: PropTypes.arrayOf(PropTypes.string).isRequired,
  initialFieldsIds: PropTypes.arrayOf(PropTypes.string).isRequired,
  initialAllowCrossDependency: PropTypes.bool,
  showFieldEditLink: PropTypes.bool.isRequired,
};
